<template>
  <div>
    <ContentWrap title="IP项目">
      <el-row :gutter="10" class="mb8">
        <el-col :span="1.5">
          <el-button type="primary" plain @click="handleAdd" v-hasPermi="['live:project:create']">
            <Icon icon="ep:plus" />新增
          </el-button>
        </el-col>
      </el-row>
    </ContentWrap>

    <ContentWrap>
      <el-table v-loading="loading" :data="list" border>
        <el-table-column label="编号" prop="id" width="80" />
        <el-table-column label="IP标题" prop="ipTitle" min-width="160" />
        <el-table-column label="IP图片" min-width="100">
          <template #default="scope">
            <el-image
              v-if="scope.row.ipImage"
              :src="scope.row.ipImage"
              style="width: 40px; height: 40px; border-radius: 4px"
              fit="cover"
            />
            <span v-else>--</span>
          </template>
        </el-table-column>
        <el-table-column label="IP描述" prop="ipDesc" min-width="200" show-overflow-tooltip />
        <el-table-column label="状态" prop="state" width="80">
          <template #default="scope">
            <el-tag :type="scope.row.state === 1 ? 'success' : 'info'">{{
              scope.row.state === 1 ? '上架' : '下架'
            }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column
          label="创建时间"
          prop="createTime"
          min-width="180"
          :formatter="dateFormatter"
        />
        <el-table-column label="操作" width="180" fixed="right">
          <template #default="scope">
            <el-button
              link
              type="primary"
              @click="handleEdit(scope.row)"
              v-hasPermi="['live:project:update']"
              >修改</el-button
            >
            <el-button
              link
              type="danger"
              @click="handleDelete(scope.row)"
              v-hasPermi="['live:project:delete']"
              >删除</el-button
            >
          </template>
        </el-table-column>
      </el-table>

      <pagination
        v-show="total > 0"
        :total="total"
        v-model:page="queryParams.pageNo"
        v-model:limit="queryParams.pageSize"
        @pagination="getList"
      />
    </ContentWrap>

    <el-dialog :title="dialogTitle" v-model="dialogVisible" width="500px" append-to-body>
      <el-form
        ref="formRef"
        :model="form"
        :rules="withLiveInputLimits(rules, liveInputLimits)"
        label-width="100px"
      >
        <el-form-item label="IP标题" prop="ipTitle">
          <el-input
            show-word-limit
            :maxlength="128"
            v-model="form.ipTitle"
            placeholder="请输入IP标题"
          />
        </el-form-item>
        <el-form-item label="IP图片" prop="ipImage">
          <UploadImg v-model="form.ipImage" />
        </el-form-item>
        <el-form-item label="IP描述" prop="ipDesc">
          <el-input
            show-word-limit
            :maxlength="1000"
            v-model="form.ipDesc"
            type="textarea"
            :rows="3"
            placeholder="请输入IP描述"
          />
        </el-form-item>
        <el-form-item label="状态" prop="state">
          <el-radio-group v-model="form.state">
            <el-radio :label="1">上架</el-radio>
            <el-radio :label="0">下架</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取 消</el-button>
        <el-button type="primary" @click="submitForm">确 定</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup lang="ts">
import { withLiveInputLimits } from '@/utils/liveInputLimits'
const liveInputLimits = { ipTitle: 128, ipDesc: 1000 }

import { dateFormatter } from '@/utils/formatTime'
import { ref, reactive, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import { createProject, updateProject, deleteProject, pageProject } from '@/api/live/project'
import { UploadImg } from '@/components/UploadFile'

const loading = ref(false)
const list = ref<any[]>([])
const total = ref(0)
const dialogVisible = ref(false)
const dialogTitle = ref('')
const formRef = ref<any>(null)

const queryParams = reactive({ pageNo: 1, pageSize: 10 })

const form = reactive({
  id: undefined as number | undefined,
  ipTitle: '',
  ipImage: '',
  ipDesc: '',
  state: 1
})

const rules = {
  ipTitle: [{ required: true, message: 'IP标题不能为空', trigger: 'blur' }],
  state: [{ required: true, message: '状态不能为空', trigger: 'change' }]
}

const getList = async () => {
  loading.value = true
  try {
    const data: any = await pageProject(queryParams)
    list.value = data.list || []
    total.value = data.total || 0
  } finally {
    loading.value = false
  }
}

const resetForm = () => {
  form.id = undefined
  form.ipTitle = ''
  form.ipImage = ''
  form.ipDesc = ''
  form.state = 1
}

const handleAdd = () => {
  resetForm()
  dialogTitle.value = '新增IP项目'
  dialogVisible.value = true
}

const handleEdit = (row: any) => {
  resetForm()
  Object.assign(form, row)
  dialogTitle.value = '修改IP项目'
  dialogVisible.value = true
}

const submitForm = async () => {
  await formRef.value?.validate()
  if (form.id) {
    await updateProject(form)
    ElMessage.success('修改成功')
  } else {
    await createProject(form)
    ElMessage.success('新增成功')
  }
  dialogVisible.value = false
  getList()
}

const handleDelete = (row: any) => {
  ElMessageBox.confirm('确认删除该IP项目吗？', '提示', { type: 'warning' }).then(async () => {
    await deleteProject(row.id)
    ElMessage.success('删除成功')
    getList()
  })
}

onMounted(() => {
  getList()
})
</script>
